import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import {
  BulkUploadDataValidationInterfaceInstanceRequest,
  bulkUploadPpsResponseInterface,
  ErrorDescriptionInterface
} from 'Interfaces/JobsInterface';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SysproJob } from 'screens/JobPaste/types';
import { JobPasteServices } from 'services/jobPasteServices';
import { handleApiError } from 'utils/handleApiError';

export const useImportSysproJobs = () => {
  const { GlobalModalStore, JobPasteStore } = useStore();
  const [selectedJobs, setSelectedJobs] = useState<SysproJob[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [jobErrors, setJobErrors] = useState<Map<string, string>>(new Map());
  const [hasValidationError, setHasValidationError] = useState(false);

  const visibility = GlobalModalStore.getCurrentInstance(
    MODAL_TYPE.PPS_JOB_PASTE_IMPORT_SYSPRO_JOBS_MODAL
  );

  const onClose = () => {
    GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_IMPORT_SYSPRO_JOBS_MODAL);
    setSelectedJobs([]);
    setJobErrors(new Map());
    setHasValidationError(false);
  };

  // Fetch Syspro jobs when the modal is opened
  useEffect(() => {
    if (visibility) {
      JobPasteStore.fetchSysproJobs();
      // Clear errors when modal opens
      setJobErrors(new Map());
      setHasValidationError(false);
    }
  }, [visibility, JobPasteStore]);

  const handleImportJobs = async () => {
    if (selectedJobs.length === 0) {
      toast.warning('Please select at least one job to import');
      return;
    }

    setIsImporting(true);
    // Clear previous errors
    setJobErrors(new Map());
    setHasValidationError(false);

    try {
      // Transform the selected Syspro jobs to the format expected by bulkUpdateJobs
      const transformedJobs: BulkUploadDataValidationInterfaceInstanceRequest[] = selectedJobs.map(
        job => ({
          jobId: job.job_id,
          ndc: job.ndc,
          room: job.room,
          priority: job.priority.toString(),
          description: job.description,
          qtyOfContainersToFill: job.qty_of_containers_to_fill.toString(),
          qtyPerContainer: job.qty_per_container.toString(),
          qtyUnitsToPackage: job.qty_units_to_package,
          runType: job.run_type,
          confirmedDate: job.confirmed_date ? new Date(job.confirmed_date) : new Date(),
          jobStartTime: job.job_start_time ? new Date(job.job_start_time) : null,
          numberOfBottlesAtShiftStart: job.number_of_bottles_at_shift_start?.toString() || null,
          status: job.status || null,
          initialNote: job.initial_note
        })
      );

      // Call the bulk update service
      const response = await JobPasteServices.bulkUpdateJobs(transformedJobs);

      // Handle the response
      if (
        response === 'Success' ||
        (typeof response === 'object' && !hasValidationErrors(response))
      ) {
        toast.success(`Successfully imported ${selectedJobs.length} jobs from Syspro`);

        // Backend handles WebSocket broadcasting automatically
        // No need to send WebSocket message from frontend
        // WebSocket will automatically update the jobs list

        // Close the modal
        onClose();
      } else {
        // Handle validation errors
        handleValidationErrors(response, transformedJobs);
        setHasValidationError(true);
      }
    } catch (error) {
      handleApiError(error, 'Failed to import Syspro jobs');
    } finally {
      setIsImporting(false);
    }
  };

  // Helper function to check if the response contains validation errors
  const hasValidationErrors = (response: bulkUploadPpsResponseInterface) => {
    if (response === 'Success') {
      return false;
    }
    return (
      response.isfileStructurePpsJobsValidity?.invalid ||
      response.isFileDatesOverlapping?.invalid ||
      response.isJobIdsUnique?.invalid ||
      response.isNDCFormatValid?.invalid
    );
  };

  // Helper function to handle validation errors
  const handleValidationErrors = (
    response: bulkUploadPpsResponseInterface,
    transformedJobs: BulkUploadDataValidationInterfaceInstanceRequest[]
  ) => {
    if (response === 'Success') {
      return;
    }

    const errorMap = new Map<string, string>();
    let errorCount = 0;

    // Process each type of validation error
    if (response.isfileStructurePpsJobsValidity?.invalid) {
      response.isfileStructurePpsJobsValidity.error_descriptions.forEach(
        (error: ErrorDescriptionInterface) => {
          if (error.row < transformedJobs.length) {
            const jobId = transformedJobs[error.row].jobId;
            errorMap.set(jobId, error.Error);
            errorCount++;
          }
        }
      );
    }

    if (response.isFileDatesOverlapping?.invalid) {
      response.isFileDatesOverlapping.error_descriptions.forEach(
        (error: ErrorDescriptionInterface) => {
          if (error.row < transformedJobs.length) {
            const jobId = transformedJobs[error.row].jobId;
            const existingError = errorMap.get(jobId);
            errorMap.set(jobId, existingError ? `${existingError}; ${error.Error}` : error.Error);
            errorCount++;
          }
        }
      );
    }

    if (response.isJobIdsUnique?.invalid) {
      response.isJobIdsUnique.error_descriptions.forEach((error: ErrorDescriptionInterface) => {
        if (error.row < transformedJobs.length) {
          const jobId = transformedJobs[error.row].jobId;
          const existingError = errorMap.get(jobId);
          errorMap.set(jobId, existingError ? `${existingError}; ${error.Error}` : error.Error);
          errorCount++;
        }
      });
    }

    if (response.isNDCFormatValid?.invalid) {
      response.isNDCFormatValid.error_descriptions.forEach((error: ErrorDescriptionInterface) => {
        if (error.row < transformedJobs.length) {
          const jobId = transformedJobs[error.row].jobId;
          const existingError = errorMap.get(jobId);
          errorMap.set(jobId, existingError ? `${existingError}; ${error.Error}` : error.Error);
          errorCount++;
        }
      });
    }

    setJobErrors(errorMap);

    // Show a summary toast
    if (errorCount > 0) {
      toast.error(`Data validation failed for ${errorMap.size} job(s). See table for details.`);
    }
  };

  return {
    visibility,
    onClose,
    isLoading: JobPasteStore.isSysproJobsLoading || isImporting,
    sysproJobs: JobPasteStore.sysproJobs,
    selectedJobs,
    setSelectedJobs,
    handleImportJobs,
    jobErrors,
    hasValidationError
  };
};
